import React, { useCallback } from 'react';
import { Group, Rect, Text, Circle, Line } from 'react-konva';
import { NodeGroup, ThemePalette, Point, KonvaMouseEvent, KonvaDragEvent } from '../../types';
import { FONT_FAMILY, scaleFont } from '../../constants/typography';
import { GROUP } from '../../constants/groups';

// Proxy socket = an aggregated external socket displayed on the collapsed group frame
export interface ProxySocket {
    id: string;
    type: 'input' | 'output';
    label: string;
    position: Point;
    originalNodeId: string;
    originalSocketId: string;
}

interface GroupFrameComponentProps {
    group: NodeGroup;
    isSelected: boolean;
    proxySockets: ProxySocket[];
    onGroupClick: (groupId: string) => void;
    onGroupDoubleClick: (groupId: string) => void;
    onGroupContextMenu: (groupId: string, x: number, y: number) => void;
    onDragMove: (groupId: string, dx: number, dy: number) => void;
    onResizeFrame?: (groupId: string, newPos: Point, newSize: { width: number; height: number }) => void;
    onEnterGroup?: (groupId: string) => void;
    palette: ThemePalette;
    scale?: number;
    isFrame?: boolean;
    isReadOnly?: boolean;
    /** The page's label multiplier. 1 leaves every size exactly as authored. */
    textScale?: number;
}

const NODE_WIDTH = 220;
const HEADER_H = 40;
const SOCKET_ROW = 22;
const BOTTOM_PAD = 12;
const INPUT_COLOR = '#10b981';
const OUTPUT_COLOR = '#818cf8';
const EXPANDED_HEADER_H = GROUP.HEADER;

export const GroupFrameComponent: React.FC<GroupFrameComponentProps> = ({
    group, isSelected, proxySockets,
    onGroupClick, onGroupDoubleClick, onGroupContextMenu, onDragMove, onResizeFrame, onEnterGroup, palette,
    scale = 1, isFrame = false, isReadOnly = false, textScale = 1,
}) => {
    const { position, size, label, color, collapsed, childNodeIds } = group;
    /** Titles and socket labels scale; the counters and buttons are chrome. */
    const fs = (px: number) => scaleFont(px, textScale);

    const proxyInputs = proxySockets.filter(s => s.type === 'input');
    const proxyOutputs = proxySockets.filter(s => s.type === 'output');
    const proxyRows = Math.max(proxyInputs.length, proxyOutputs.length, 1);
    const collapsedH = HEADER_H + proxyRows * SOCKET_ROW + BOTTOM_PAD;

    const w = collapsed ? NODE_WIDTH : size.width;
    const h = collapsed ? collapsedH : size.height;
    const canDragGroup = !isReadOnly || isFrame;

    const handleClick = useCallback((e: KonvaMouseEvent) => {
        e.cancelBubble = true;
        onGroupClick(group.id);
    }, [group.id, onGroupClick]);

    const handleDblClick = useCallback((e: KonvaMouseEvent) => {
        e.cancelBubble = true;
        onGroupDoubleClick(group.id);
    }, [group.id, onGroupDoubleClick]);

    const handleContextMenu = useCallback((e: KonvaMouseEvent) => {
        e.cancelBubble = true;
        e.evt.preventDefault();
        onGroupContextMenu(group.id, e.evt.clientX, e.evt.clientY);
    }, [group.id, onGroupContextMenu]);

    // Header drag: delta-reset pattern since header is child of positioned Group
    const handleHeaderDragMove = useCallback((e: KonvaDragEvent) => {
        const dx = e.target.x();
        const dy = e.target.y();
        e.target.x(0);
        e.target.y(0);
        if (dx !== 0 || dy !== 0) {
            onDragMove(group.id, dx, dy);
        }
    }, [group.id, onDragMove]);

    const handleMouseEnter = useCallback((e: KonvaMouseEvent) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = canDragGroup ? 'grab' : 'default';
    }, [canDragGroup]);

    const handleMouseLeave = useCallback((e: KonvaMouseEvent) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = 'default';
    }, []);

    /* ── EXPANDED MODE ───────────────────────────────────────────────────
       - Body frame is CLICKABLE (for group selection on empty space)
         but NOT draggable. Konva z-order ensures nodes on top get priority.
       - Only the header bar is draggable (for moving the whole group).
       ───────────────────────────────────────────────────────────────────── */
    if (!collapsed) {
        if (isFrame) {
            const frameColor = group.color ?? '#f59e0b';
            // Parse frameColor to rgba for fills — use it directly as hex for strokes
            const frameFill = `${frameColor}10`;
            const frameStroke = isSelected ? `${frameColor}cc` : `${frameColor}66`;
            const headerFill = `${frameColor}1e`;
            const sw = 1.5 / scale;
            const hs = Math.max(6, 8 / scale); // handle size, scale-invariant

            // Delta-reset resize: each handle lives at a non-zero offset within the group,
            // so we reset to its INITIAL position (not 0,0) and compute delta relative to that.
            const makeResizeHandler = (handle: string, initX: number, initY: number) => (e: KonvaDragEvent) => {
                const dx = e.target.x() - initX;
                const dy = e.target.y() - initY;
                e.target.x(initX);
                e.target.y(initY);
                if ((dx === 0 && dy === 0) || !onResizeFrame) return;
                let nx = position.x, ny = position.y, nw = w, nh = h;
                if (handle.includes('w')) { nx += dx; nw -= dx; }
                if (handle.includes('e')) { nw += dx; }
                if (handle.includes('n')) { ny += dy; nh -= dy; }
                if (handle.includes('s')) { nh += dy; }
                if (nw < 80 || nh < 60) return;
                onResizeFrame(group.id, { x: nx, y: ny }, { width: nw, height: nh });
            };

            const handles = [
                { id: 'nw', x: 0,   y: 0   }, { id: 'n',  x: w/2, y: 0   }, { id: 'ne', x: w,   y: 0   },
                { id: 'e',  x: w,   y: h/2 }, { id: 'se', x: w,   y: h   }, { id: 's',  x: w/2, y: h   },
                { id: 'sw', x: 0,   y: h   }, { id: 'w',  x: 0,   y: h/2 },
            ];

            const cursorMap: Record<string, string> = {
                nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize',
                e: 'e-resize', se: 'se-resize', s: 's-resize',
                sw: 'sw-resize', w: 'w-resize',
            };

            return (
                <Group x={position.x} y={position.y}>
                    {/* Body */}
                    <Rect
                        width={w} height={h}
                        fill={frameFill}
                        stroke={frameStroke}
                        strokeWidth={sw} cornerRadius={16}
                        onClick={handleClick}
                        onDblClick={handleDblClick}
                        onContextMenu={handleContextMenu}
                        perfectDrawEnabled={false}
                    />
                    {/* Header bar — DRAGGABLE */}
                    <Rect
                        width={w} height={EXPANDED_HEADER_H}
                        fill={headerFill}
                        cornerRadius={[16, 16, 0, 0]}
                        draggable={canDragGroup}
                        onDragMove={handleHeaderDragMove}
                        onClick={handleClick}
                        onDblClick={handleDblClick}
                        onContextMenu={handleContextMenu}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        perfectDrawEnabled={false}
                    />
                    {/* Label — mermaid subgraphs show the title only, not "FRAME" chrome */}
                    <Text x={12} y={6} width={isSelected ? w - 70 : w - 24} text={label}
                        fontSize={fs(11)} fontFamily={FONT_FAMILY} fontStyle="600"
                        fill={`${frameColor}e6`} listening={false} perfectDrawEnabled={false}
                    />
                    {isSelected && (
                    <Text x={w - 56} y={8} width={46}
                        text={`${childNodeIds.length} nodes`}
                        fontSize={9} fontFamily={FONT_FAMILY}
                        fill={`${frameColor}99`} align="right"
                        listening={false} perfectDrawEnabled={false}
                    />
                    )}
                    {/* Resize handles — only on the selected frame so imports aren't a handle grid */}
                    {!isReadOnly && isSelected && handles.map(hnd => {
                        const hx = hnd.x - hs / 2;
                        const hy = hnd.y - hs / 2;
                        return (
                        <Rect
                            key={hnd.id}
                            x={hx} y={hy}
                            width={hs} height={hs}
                            fill={frameColor} opacity={0.7}
                            cornerRadius={2 / scale}
                            draggable={!isReadOnly}
                            onDragMove={makeResizeHandler(hnd.id, hx, hy)}
                            onMouseEnter={(e: KonvaMouseEvent) => {
                                const c = e.target.getStage()?.container();
                                if (c) c.style.cursor = cursorMap[hnd.id];
                            }}
                            onMouseLeave={(e: KonvaMouseEvent) => {
                                const c = e.target.getStage()?.container();
                                if (c) c.style.cursor = 'default';
                            }}
                            perfectDrawEnabled={false}
                        />
                        );
                    })}
                </Group>
            );
        }

        return (
            <Group x={position.x} y={position.y}>
                {/* Selection ring */}
                {isSelected && (
                    <Rect
                        x={-3} y={-3} width={w + 6} height={h + 6}
                        cornerRadius={12} stroke={color} strokeWidth={2}
                        dash={[8, 4]} fill="transparent"
                        listening={false} perfectDrawEnabled={false}
                    />
                )}

                {/* Dashed frame body — clickable for group selection,
            but nodes on top take priority via Konva z-order */}
                <Rect
                    width={w} height={h}
                    fill={palette.groupFrameBg}
                    stroke={isSelected ? color : palette.groupFrameBorder}
                    strokeWidth={1.5} cornerRadius={10} dash={[8, 4]}
                    onClick={handleClick}
                    onDblClick={handleDblClick}
                    onContextMenu={handleContextMenu}
                    perfectDrawEnabled={false}
                />

                {/* Header bar — DRAGGABLE for moving entire group */}
                <Rect
                    width={w} height={EXPANDED_HEADER_H}
                    fill={`${color}18`}
                    cornerRadius={[10, 10, 0, 0]}
                    draggable={canDragGroup}
                    onDragMove={handleHeaderDragMove}
                    onClick={handleClick}
                    onDblClick={handleDblClick}
                    onContextMenu={handleContextMenu}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    perfectDrawEnabled={false}
                />

                {/* Group icon: overlapping squares */}
                <Rect x={12} y={8} width={8} height={8} cornerRadius={2} stroke={color} strokeWidth={1.2} fill="transparent" listening={false} perfectDrawEnabled={false} />
                <Rect x={16} y={4} width={8} height={8} cornerRadius={2} stroke={color} strokeWidth={1.2} fill={palette.groupFrameBg} listening={false} perfectDrawEnabled={false} />

                {/* Label */}
                <Text x={30} y={6} width={w - 90} text={label}
                    fontSize={fs(11)} fontFamily={FONT_FAMILY} fontStyle="600"
                    fill={palette.groupLabelText} listening={false} perfectDrawEnabled={false}
                />

                {/* Child count */}
                <Text x={w - 80} y={8} width={46}
                    text={`${childNodeIds.length} nodes`}
                    fontSize={9} fontFamily={FONT_FAMILY}
                    fill={palette.panelSubText} align="right"
                    listening={false} perfectDrawEnabled={false}
                />

                {/* Enter group button (Tab shortcut indicator) */}
                {onEnterGroup && (
                    <Text
                        x={w - 28} y={6} width={20}
                        text=">"
                        fontSize={11} fontFamily={FONT_FAMILY}
                        fill={color} opacity={0.8} align="right"
                        onClick={(e: KonvaMouseEvent) => { e.cancelBubble = true; onEnterGroup(group.id); }}
                        onMouseEnter={(e: KonvaMouseEvent) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'pointer'; }}
                        onMouseLeave={(e: KonvaMouseEvent) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'default'; }}
                        perfectDrawEnabled={false}
                    />
                )}
            </Group>
        );
    }

    /* ── COLLAPSED MODE: proxy node ────────────────────────────────────── */
    const halfW = w / 2;

    return (
        <Group
            x={position.x} y={position.y}
            draggable={canDragGroup}
            onDragMove={(e: KonvaDragEvent) => {
                const dx = e.target.x() - position.x;
                const dy = e.target.y() - position.y;
                onDragMove(group.id, dx, dy);
            }}
            onClick={handleClick}
            onDblClick={handleDblClick}
            onContextMenu={handleContextMenu}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {isSelected && (
                <Rect x={-4} y={-4} width={w + 8} height={h + 8}
                    cornerRadius={14} stroke={color} strokeWidth={1.5}
                    dash={[6, 4]} fill="transparent"
                    listening={false} perfectDrawEnabled={false}
                />
            )}

            <Rect x={2} y={4} width={w} height={h} cornerRadius={10} fill={palette.nodeShadow} listening={false} perfectDrawEnabled={false} />
            <Rect width={w} height={h} fill={palette.nodeBody} stroke={isSelected ? color : palette.nodeStroke}
                strokeWidth={isSelected ? 1.5 : 1} cornerRadius={10} perfectDrawEnabled={false} />
            <Rect x={0} y={0} width={5} height={h} fill={color} cornerRadius={[10, 0, 0, 10]} listening={false} perfectDrawEnabled={false} />
            <Rect x={5} y={0} width={w - 5} height={1} fill={palette.nodeHighlight} listening={false} perfectDrawEnabled={false} />

            <Text x={14} y={10} text="GROUP" fontSize={fs(8)} fontFamily={FONT_FAMILY} fontStyle="bold"
                fill={color} opacity={0.9} listening={false} perfectDrawEnabled={false} />
            <Text x={14} y={22} width={w - 50} text={`${label} (${childNodeIds.length})`}
                fontSize={fs(13)} fontFamily={FONT_FAMILY} fontStyle="600"
                fill={palette.nodeText} listening={false} perfectDrawEnabled={false} />
            {/* Enter group button */}
            {onEnterGroup && (
                <Text
                    x={w - 36} y={12} width={28}
                    text="> Enter"
                    fontSize={8} fontFamily={FONT_FAMILY}
                    fill={color} opacity={0.85} align="right"
                    onClick={(e: KonvaMouseEvent) => { e.cancelBubble = true; onEnterGroup(group.id); }}
                    onMouseEnter={(e: KonvaMouseEvent) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'pointer'; }}
                    onMouseLeave={(e: KonvaMouseEvent) => { const c = e.target.getStage()?.container(); if (c) c.style.cursor = 'default'; }}
                    perfectDrawEnabled={false}
                />
            )}

            {/* Proxy socket labels */}
            {proxyInputs.map(ps => (
                <Text key={`lbl-${ps.id}`} x={12} y={ps.position.y - 6} width={halfW - 16}
                    text={ps.label} fontSize={fs(10)} fontFamily={FONT_FAMILY}
                    fill={palette.nodeSubText} align="left" listening={false} perfectDrawEnabled={false} />
            ))}
            {proxyOutputs.map(ps => (
                <Text key={`lbl-${ps.id}`} x={halfW + 4} y={ps.position.y - 6} width={halfW - 18}
                    text={ps.label} fontSize={fs(10)} fontFamily={FONT_FAMILY}
                    fill={palette.nodeSubText} align="right" listening={false} perfectDrawEnabled={false} />
            ))}

            {/* Socket circles */}
            {proxySockets.map(ps => {
                const baseColor = ps.type === 'input' ? INPUT_COLOR : OUTPUT_COLOR;
                const tickLen = 8;
                const tickPoints = ps.type === 'input'
                    ? [ps.position.x - 2, ps.position.y, ps.position.x - tickLen, ps.position.y]
                    : [ps.position.x + 2, ps.position.y, ps.position.x + tickLen, ps.position.y];
                return (
                    <Group key={ps.id}>
                        <Line points={tickPoints} stroke={baseColor} strokeWidth={1.5} listening={false} perfectDrawEnabled={false} />
                        <Circle x={ps.position.x} y={ps.position.y} radius={5.5}
                            fill={baseColor} stroke={baseColor} strokeWidth={1.5}
                            shadowColor={baseColor} shadowBlur={4} listening={false} perfectDrawEnabled={false} />
                    </Group>
                );
            })}
        </Group>
    );
};

// ── Utility: compute proxy sockets for a collapsed group ──────────────────────
export function computeProxySockets(
    group: NodeGroup,
    allNodes: { id: string; position?: { x: number; y: number }; sockets: { id: string; type: 'input' | 'output'; label?: string }[] }[],
    allEdges: { sourceNodeId: string; sourceSocketId: string; targetNodeId: string; targetSocketId: string }[],
): ProxySocket[] {
    const childSet = new Set(group.childNodeIds);

    // Collect all crossing edges, deduplicated by socket key
    interface Candidate { key: string; type: 'input' | 'output'; nodeId: string; socketId: string; nodeY: number; }
    const candidateMap = new Map<string, Candidate>();

    for (const edge of allEdges) {
        const srcInside = childSet.has(edge.sourceNodeId);
        const tgtInside = childSet.has(edge.targetNodeId);

        if (srcInside && !tgtInside) {
            const key = `out:${edge.sourceNodeId}:${edge.sourceSocketId}`;
            if (!candidateMap.has(key)) {
                const srcNode = allNodes.find(n => n.id === edge.sourceNodeId);
                candidateMap.set(key, { key, type: 'output', nodeId: edge.sourceNodeId, socketId: edge.sourceSocketId, nodeY: srcNode?.position?.y ?? 0 });
            }
        } else if (!srcInside && tgtInside) {
            const key = `in:${edge.targetNodeId}:${edge.targetSocketId}`;
            if (!candidateMap.has(key)) {
                const tgtNode = allNodes.find(n => n.id === edge.targetNodeId);
                candidateMap.set(key, { key, type: 'input', nodeId: edge.targetNodeId, socketId: edge.targetSocketId, nodeY: tgtNode?.position?.y ?? 0 });
            }
        }
    }

    // Sort by node Y position for deterministic ordering
    const inputs = [...candidateMap.values()].filter(c => c.type === 'input').sort((a, b) => a.nodeY - b.nodeY);
    const outputs = [...candidateMap.values()].filter(c => c.type === 'output').sort((a, b) => a.nodeY - b.nodeY);

    const proxies: ProxySocket[] = [];

    inputs.forEach((c, inputIdx) => {
        const node = allNodes.find(n => n.id === c.nodeId);
        const socket = node?.sockets.find(s => s.id === c.socketId);
        proxies.push({
            id: `proxy-in-${inputIdx}`, type: 'input',
            label: socket?.label ?? `In ${inputIdx + 1}`,
            position: { x: 0, y: HEADER_H + inputIdx * SOCKET_ROW + SOCKET_ROW / 2 },
            originalNodeId: c.nodeId, originalSocketId: c.socketId,
        });
    });

    outputs.forEach((c, outputIdx) => {
        const node = allNodes.find(n => n.id === c.nodeId);
        const socket = node?.sockets.find(s => s.id === c.socketId);
        proxies.push({
            id: `proxy-out-${outputIdx}`, type: 'output',
            label: socket?.label ?? `Out ${outputIdx + 1}`,
            position: { x: NODE_WIDTH, y: HEADER_H + outputIdx * SOCKET_ROW + SOCKET_ROW / 2 },
            originalNodeId: c.nodeId, originalSocketId: c.socketId,
        });
    });

    return proxies;
}
